import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72034c2-4f95-4f18-be74-1d29b1871fdc/artifacts/djdyqu9t_IMG_1381.png";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const user = await login(email, password);
      if (user.has_paid) navigate("/app/feed");
      else navigate("/payment");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5EEDB] flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Refugium" className="w-16 h-16 rounded-full mx-auto mb-4" />
          <h1 className="font-heading text-3xl text-[#5B3E2E]">Bienvenido de nuevo</h1>
          <p className="text-sm text-[#5B3E2E]/60 mt-2">Inicia sesión en tu espacio seguro</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#FDFAF4] rounded-3xl p-8 border border-[#5B3E2E]/10 shadow-[0_8px_30px_rgba(91,62,46,0.06)]">
          {error && (
            <div data-testid="login-error" className="bg-[#D9534F]/10 text-[#D9534F] text-sm rounded-xl p-3 mb-4">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-[#5B3E2E] text-sm">Email</Label>
              <Input
                data-testid="login-email"
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl focus:ring-1 focus:ring-[#E48D57] text-[#5B3E2E]"
                placeholder="tu@email.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#5B3E2E] text-sm">Contraseña</Label>
              <div className="relative mt-1">
                <Input
                  data-testid="login-password"
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-transparent border-[#5B3E2E]/20 rounded-xl focus:ring-1 focus:ring-[#E48D57] text-[#5B3E2E] pr-10"
                  placeholder="Tu contraseña"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B3E2E]/40"
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={submitting}
            className="w-full mt-6 bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full py-3 font-medium transition-all"
          >
            {submitting ? "Entrando..." : "Iniciar sesión"}
          </Button>
        </form>

        <p className="text-center text-sm text-[#5B3E2E]/60 mt-6">
          ¿No tienes cuenta?{" "}
          <Link to="/register" data-testid="go-to-register" className="text-[#E48D57] hover:underline font-medium">
            Regístrate aquí
          </Link>
        </p>
        <p className="text-center mt-3">
          <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-[#5B3E2E]/40 hover:text-[#5B3E2E]/60">
            ¿Has olvidado tu contraseña?
          </Link>
        </p>
        <p className="text-center mt-4">
          <Link to="/" className="text-sm text-[#5B3E2E]/40 hover:text-[#5B3E2E]/60">Volver al inicio</Link>
        </p>
      </div>
    </div>
  );
}
